import { Link } from "react-router-dom";
import { ShieldCheck, Percent, AlertTriangle, Handshake } from "lucide-react";

export default function Terms() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <h1 className="heading text-3xl sm:text-4xl font-extrabold tracking-tight">Terms &amp; Conditions</h1>
      <p className="text-stone-500 mt-2 text-sm">Last updated: August 2026 · GeneticsTrade</p>

      <div className="mt-8 space-y-6">
        <section className="surface-card p-6" data-testid="terms-fees">
          <div className="flex items-center gap-2">
            <Percent className="h-5 w-5 text-[color:var(--earth-amber)]" />
            <h2 className="heading text-xl font-bold">Listing &amp; Sales Fees</h2>
          </div>
          <p className="mt-3 text-stone-700 leading-relaxed">
            GeneticsTrade charges a <strong>6% fee on sales</strong>. The fee is calculated automatically on every
            order and <strong>displayed openly to both sellers and buyers</strong> before any funds are committed —
            in the purchase dialog, on the order page, and in payout summaries. There are no hidden charges.
          </p>
          <div className="mt-4 border border-stone-200 rounded-lg overflow-hidden">
            <table className="data-table">
              <thead><tr><th>Line</th><th>Example (sale of $1,000)</th></tr></thead>
              <tbody>
                <tr><td>Sale total (held in escrow)</td><td className="mono">$1,000.00</td></tr>
                <tr><td>Platform fee (6%)</td><td className="mono">−$60.00</td></tr>
                <tr><td>Seller payout on delivery confirmation</td><td className="mono font-semibold">$940.00</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="surface-card p-6" data-testid="terms-responsibility">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[color:var(--forest-deep)]" />
            <h2 className="heading text-xl font-bold">Provider Responsibility</h2>
          </div>
          <p className="mt-3 text-stone-700 leading-relaxed">
            <strong>Services and products are the sole responsibility of the Provider.</strong> Sellers and service
            providers are fully responsible for the accuracy of their listings, genetic data, pedigree records,
            health certifications, and the quality and legality of the genetic material or services supplied.
          </p>
        </section>

        <section className="surface-card p-6" data-testid="terms-customs">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[color:var(--forest-deep)]" />
            <h2 className="heading text-xl font-bold">International Shipping, Taxes &amp; Customs</h2>
          </div>
          <p className="mt-3 text-stone-700 leading-relaxed">
            <strong>Additional taxes and customs/import fees on international shipping may apply and are not the
            responsibility of GeneticsTrade.</strong> See your local area's regulations for any additional fees you
            may incur. Buyers are responsible for duties, tariffs, and import compliance in their jurisdiction.
          </p>
        </section>

        <section className="surface-card p-6" data-testid="terms-conduct">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <h2 className="heading text-xl font-bold">Misleading Conduct</h2>
          </div>
          <p className="mt-3 text-stone-700 leading-relaxed">
            <strong>Any misleading conduct will not be tolerated on the site, and the offending account will be
            banned as a result.</strong> This includes falsified pedigree or genetic data, forged health
            certificates, misrepresented conservation status, and fraudulent escrow or shipment claims.
          </p>
        </section>

        <section className="surface-card p-6" data-testid="terms-agent">
          <div className="flex items-center gap-2">
            <Handshake className="h-5 w-5 text-[color:var(--bio-frost)]" />
            <h2 className="heading text-xl font-bold">Platform Role</h2>
          </div>
          <p className="mt-3 text-stone-700 leading-relaxed">
            <strong>GeneticsTrade is a third-party agent</strong> that facilitates a connection between a
            seller/service provider and a consumer. <strong>It has no interests in any products or services outside
            of the website's stated purpose</strong> — the verified, escrow-protected trade of livestock genetic
            material and accredited transport and veterinary services.
          </p>
        </section>
      </div>

      <p className="mt-8 text-sm text-stone-500 text-center">
        Questions about these terms? <Link to="/marketplace" className="underline text-[color:var(--forest-deep)]">Return to the marketplace</Link>
      </p>
    </div>
  );
}
